<template>
  <section>
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ formTitle }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group horizontal name="sort" :label="$t('attributes.sort')">
              <b-select v-model="form.sort" name="sort">
                <option
                  v-for="option in sortOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </form-group>

            <form-group
              horizontal
              name="status"
              attribute="attributes.status"
              :label="$t('attributes.status')"
            >
              <b-switch v-model="form.status" name="status"></b-switch>
            </form-group>

            <form-group horizontal name="name" :label="$t('attributes.name')">
              <b-input
                v-model="form.name"
                name="name"
                :placeholder="
                  $t('message.please_input') + $t('attributes.name')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="effect_dates"
              attribute="attributes.effect_date"
              :label="$t('attributes.effect_date')"
            >
              <b-datepicker
                v-model="form.effect_dates"
                icon="calendar-today"
                trap-focus
                range
                :date-formatter="formatDates"
                :placeholder="
                  $t('message.please_select') + $t('attributes.effect_date')
                "
                @input="onSelectEffectDates"
              >
              </b-datepicker>
            </form-group>

            <form-group
              horizontal
              name="picture"
              attribute="attributes.picture"
              :label="$t('attributes.picture')"
              grouped
            >
              <template #label>
                {{ $t('attributes.picture') }}
                <b-tooltip type="is-dark" label="1920*640/限定jpg檔，600KB內">
                  <b-icon
                    size="is-small"
                    type="is-danger"
                    icon="help-circle-outline"
                  ></b-icon>
                </b-tooltip>
              </template>
              <b-field class="file">
                <a class="button is-primary" @click="onSelectPictureImage">
                  <b-icon icon="upload"></b-icon>
                  <span>{{ $t('message.please_select_file') }}</span>
                </a>
                <p
                  v-if="form.picture"
                  class="tag is-warning"
                  style="margin-left: 4px; height: 40px"
                >
                  <a
                    :href="bannerImage(form.picture)"
                    @click.prevent="onClickImage(bannerImage(form.picture))"
                  >
                    {{ form.picture }}
                  </a>
                </p>
              </b-field>
            </form-group>

            <form-group
              horizontal
              name="mobile_picture"
              attribute="attributes.mobile_picture"
              :label="$t('attributes.mobile_picture')"
              grouped
            >
              <template #label>
                {{ $t('attributes.mobile_picture') }}
                <b-tooltip type="is-dark" label="480*320/限定jpg檔，100KB內">
                  <b-icon
                    size="is-small"
                    type="is-danger"
                    icon="help-circle-outline"
                  ></b-icon>
                </b-tooltip>
              </template>
              <b-field class="file">
                <a
                  class="button is-primary"
                  @click="onSelectMobilePictureImage"
                >
                  <b-icon icon="upload"></b-icon>
                  <span>{{ $t('message.please_select_file') }}</span>
                </a>
                <p
                  v-if="form.mobile_picture"
                  class="tag is-warning"
                  style="margin-left: 4px; height: 40px"
                >
                  <a
                    :href="bannerImage(form.mobile_picture)"
                    @click.prevent="
                      onClickImage(bannerImage(form.mobile_picture))
                    "
                  >
                    {{ form.mobile_picture }}
                  </a>
                </p>
              </b-field>
            </form-group>

            <form-group
              horizontal
              name="picture_alt"
              attribute="attributes.image_alt"
              :label="$t('attributes.image_alt')"
            >
              <b-input
                v-model="form.picture_alt"
                name="picture_alt"
                :placeholder="
                  $t('message.please_input') + $t('attributes.image_alt')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="has_video"
              attribute="attributes.has_video"
              :label="$t('attributes.has_video')"
            >
              <b-switch v-model="form.has_video" name="has_video"></b-switch>
            </form-group>

            <form-group
              v-if="form.has_video"
              horizontal
              name="youtube_id"
              attribute="attributes.youtube_id"
              :label="$t('attributes.youtube_id')"
            >
              <b-field>
                <b-input
                  v-model="form.youtube_id"
                  name="youtube_id"
                  :placeholder="
                    $t('message.please_input') + $t('attributes.youtube_id')
                  "
                  expanded
                  @input="onChangeId"
                >
                </b-input>
                <p class="control">
                  <b-button
                    class="button"
                    @click.prevent="
                      onClickImage(helpImageUrl('youtube_id.png'))
                    "
                  >
                    說明
                  </b-button>
                </p>
              </b-field>
            </form-group>

            <form-group horizontal name="url" :label="$t('attributes.url')">
              <b-input
                v-model="form.url"
                name="url"
                :disabled="form.has_video"
                :placeholder="$t('message.please_input') + $t('attributes.url')"
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="description"
              :label="$t('attributes.description')"
            >
              <b-input
                v-model="form.description"
                name="description"
                type="textarea"
                :placeholder="
                  $t('message.please_input') + $t('attributes.description')
                "
                maxlength="500"
                expanded
              >
              </b-input>
            </form-group>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button
            :loading="isLoading"
            class="is-fullwidth"
            @click="$parent.close()"
          >
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="isLoading"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{
              this.banner.id ? $t('attributes.update') : $t('attributes.add')
            }}
          </b-button>
        </footer>

        <b-loading :active.sync="isLoading" :can-cancel="false"></b-loading>
      </div>
    </form>

    <b-modal :active.sync="isImageModalActive">
      <p class="image">
        <img :src="selectedImageUrl" alt="" />
      </p>
    </b-modal>
  </section>
</template>

<script>
import {
  required,
  maxLength,
  minLength,
  requiredIf
} from 'vuelidate/lib/validators'
import { OpenBannerFinder } from '@/utils/ckfinder'
import notifyService from '@/services/notifyService'

export default {
  name: 'BannerForm',

  props: {
    banner: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      sending: false,
      form: this.extractFormValues(this.banner),

      sortOptions: [...Array(20).keys()].map(i => i + 1),

      isImageModalActive: false,
      selectedImageUrl: null
    }
  },
  validations: {
    form: {
      sort: { required },
      status: { required },
      name: { required, maxLength: maxLength(150) },
      description: { maxLength: maxLength(500) },
      picture: { required },
      mobile_picture: { required },
      picture_alt: {},
      has_video: { required },
      youtube_id: {
        requiredIf: requiredIf(function(model) {
          return model.has_video
        })
      },
      url: {
        requiredIf: requiredIf(function(model) {
          return model.has_video
        }),
        maxLength: maxLength(200)
      },
      effect_dates: {
        required,
        minLength: minLength(2)
      },
      start_date: { required },
      end_date: { required }
    }
  },
  computed: {
    formTitle() {
      let title = this.banner.id
        ? this.$t('attributes.modify')
        : this.$t('attributes.add')

      title += this.$t('attributes.banner')

      return title
    },
    isLoading() {
      return this.sending
    }
  },
  methods: {
    extractFormValues(data) {
      let effect_dates = []

      if (data.start_date && data.end_date) {
        effect_dates = [new Date(data.start_date), new Date(data.end_date)]
      }

      return {
        id: data.id,
        sort: data.sort,
        status: data.status,
        name: data.name,
        description: data.description,
        picture: data.picture,
        mobile_picture: data.mobile_picture,
        picture_alt: data.picture_alt,
        has_video: data.has_video,
        youtube_id: this.getVideoId(data),
        url: data.url,
        effect_dates: effect_dates,
        start_date: data.start_date,
        end_date: data.end_date
      }
    },
    onSelectEffectDates() {
      this.form.start_date = this.formatDate(this.form.effect_dates[0])
      this.form.end_date = this.formatDate(this.form.effect_dates[1])
    },
    getVideoId(banner) {
      if (!banner.has_video) {
        return null
      }

      const url = new URL(banner.url)
      const prefix = '/embed/'

      if (url.host !== 'www.youtube.com') {
        return null
      }

      if (!url.pathname.startsWith(prefix)) {
        return null
      }

      return url.pathname.slice(prefix.length)
    },
    onChangeId(id) {
      if (!id) {
        return
      }

      this.form.url = `https://www.youtube.com/embed/${id}?loop=1&playlist=${id}&showinfo=0&controls=0&modestbranding=0&fs=0&autoplay=1&rel=0&autohide=0`
    },
    async onSelectPictureImage() {
      try {
        this.form.picture = await OpenBannerFinder()
      } catch (e) {
        notifyService.error(e)
      }
    },
    async onSelectMobilePictureImage() {
      try {
        this.form.mobile_picture = await OpenBannerFinder()
      } catch (e) {
        notifyService.error(e)
      }
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = url
    },
    async onSend() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
